import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Copy,
  Download,
  ExternalLink,
  FileCheck2,
  FileText,
  Landmark,
  Link2,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Save,
  Tag,
  UserRound,
} from 'lucide-react';
import { api } from '@/lib/api';
import { fmtDate, fmtInt, fmtRelative, fmtUsd } from '@/lib/format';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { AdjustToMatchPanel } from '@/components/AdjustToMatchPanel';
import { ConfidenceBadge, CountdownDisplay } from '@/components/DeadlineBadge';
import { DraftReader } from '@/components/DraftReader';
import { PolygonMap } from '@/components/PolygonMap';
import { RLPNotificationBanner } from '@/components/RLPNotificationBanner';
import { RlpActionBar } from '@/components/RlpActionBar';
import { EmptyState } from '@/components/EmptyState';
import { SkeletonLine } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { useOfferorProfiles, useStartFormFill } from '@/lib/forms';
import { OfferorProfileDialog } from '@/components/OfferorProfileDialog';
import type {
  CandidateProperty,
  OutreachDraft,
  RLPAttachment,
  RLPContact,
  RLPLink,
  RLPOfficeAddress,
  RLPOpportunity,
  RLPRequirements,
} from '@/lib/api-types';

export default function RLPDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['rlp', id],
    queryFn: () => api.getRlp(id),
    enabled: id.length > 0,
  });

  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="card flex flex-col gap-4 p-8">
        <SkeletonLine width="40%" />
        <SkeletonLine width="70%" />
        <SkeletonLine width="55%" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
        Failed to load RLP: {error instanceof Error ? error.message : 'not found'}
      </div>
    );
  }

  const { rlp, requirements, candidates, drafts, runs } = data;
  const attachments = resolveAttachments(rlp);
  const links = resolveLinks(rlp);
  const primary = resolveContact(rlp);
  const alternate = resolveAlternate(rlp);
  const office = resolveOfficeAddress(rlp);
  const classification = resolveClassification(rlp);
  const noticeMeta = resolveNoticeMeta(rlp);
  const description = resolveDescription(rlp);
  const isActive = rlp.is_active ?? rlp.extraction_status !== 'failed';

  return (
    <div className="flex flex-col gap-6" data-testid="rlp-detail">
      {/* Breadcrumb */}
      <Link
        to="/rlps"
        className="inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to solicitations
      </Link>

      {/* Header card */}
      <header className="card overflow-hidden">
        <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-[0.6875rem] font-bold uppercase tracking-kicker text-brand-700">
                Opportunity
              </span>
              <StatusPill active={isActive} />
              <span className="font-mono text-[0.8125rem] text-ink-muted">
                Notice ID · {rlp.sam_notice_id}
              </span>
            </div>
            <h1 className="mt-4 text-[1.75rem] font-bold leading-tight tracking-head text-ink lg:text-[2rem]">
              {rlp.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.8125rem] text-ink-muted">
              <span className="inline-flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                RLP {rlp.solicitation_number}
                {rlp.mod_number > 0 ? `-${String(rlp.mod_number).padStart(2, '0')}` : ''}
              </span>
              <span className="h-1 w-1 rounded-full bg-ink-dim" aria-hidden />
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                {rlp.city && rlp.state
                  ? `${rlp.city}, ${rlp.state}`
                  : (rlp.state ?? 'Location TBD')}
              </span>
              <span className="h-1 w-1 rounded-full bg-ink-dim" aria-hidden />
              <span>
                {attachments.length || rlp.pdfs_downloaded} PDF
                {(attachments.length || rlp.pdfs_downloaded) === 1 ? '' : 's'} on file
              </span>
              {requirements && (
                <>
                  <span className="h-1 w-1 rounded-full bg-ink-dim" aria-hidden />
                  <ConfidenceBadge confidence={requirements.extraction_confidence} />
                </>
              )}
            </div>
          </div>

          <div className="shrink-0 rounded-xl bg-surface-sunk px-6 py-5 lg:min-w-[18rem]">
            <CountdownDisplay deadline={rlp.response_deadline} />
            <div className="mt-3 text-right text-[0.6875rem] font-medium text-ink-muted">
              Due {fmtDate(rlp.response_deadline)}
            </div>
            {rlp.sam_url && (
              <div className="mt-3 flex justify-end">
                <a
                  href={rlp.sam_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[0.75rem] font-semibold text-brand-700 hover:text-brand-800"
                >
                  <ExternalLink className="h-3 w-3" /> View on SAM.gov
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Metadata grid — SAM.gov style */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-rule bg-surface-sunk px-6 py-5 md:grid-cols-4">
          <MetaItem label="Contract Opportunity Type" value={noticeMeta.contract_type} />
          <MetaItem label="Set Aside" value={noticeMeta.set_aside} />
          <MetaItem label="All Dates/Times in" value="Eastern Time (EDT)" />
          <MetaItem label="Published Date" value={fmtDate(rlp.posted_at)} />
          <MetaItem
            label="Date Offers Due"
            value={rlp.response_deadline ? fmtDate(rlp.response_deadline) : '—'}
            emphasis
          />
          <MetaItem
            label="Inactive Date"
            value={rlp.inactive_date ? fmtDate(rlp.inactive_date) : '—'}
          />
          <MetaItem label="Inactive Policy" value={noticeMeta.inactive_policy} />
          <MetaItem label="Related Notice" value={noticeMeta.related_notice} />
          <MetaItem label="Contract Line Item #" value={noticeMeta.contract_line_item_number} />
          <MetaItem label="Original Notice ID" value={rlp.sam_notice_id} mono />
          <MetaItem label="Updated Date" value={fmtDate(rlp.updated_at)} />
          <MetaItem label="Original Publish" value={fmtDate(rlp.posted_at)} />
        </div>
      </header>

      {/* Dept / Subtier / Office strip */}
      <div className="card overflow-hidden border-brand-100 bg-brand-50/60">
        <div className="grid grid-cols-1 divide-y divide-brand-100 md:grid-cols-3 md:divide-x md:divide-y-0">
          <OrgStripItem label="Department / Ind. Agency" value={rlp.agency} />
          <OrgStripItem label="Sub-tier" value={rlp.sub_agency} />
          <OrgStripItem
            label="Office"
            value={
              rlp.office ??
              (rlp.sub_agency ? `${rlp.sub_agency} — Leasing Division` : 'Leasing Division')
            }
          />
        </div>
      </div>

      <RLPNotificationBanner rlpId={rlp.id} />

      <RlpActionBar rlpId={rlp.id} extractionStatus={rlp.extraction_status} />

      <Tabs defaultValue="notice">
        <TabsList>
          <TabsTrigger value="notice" data-testid="tab-notice">
            Notice
          </TabsTrigger>
          <TabsTrigger value="requirements" data-testid="tab-requirements">
            Requirements
          </TabsTrigger>
          <TabsTrigger value="attachments" data-testid="tab-attachments">
            Attachments / Links
            <Badge tone="neutral" className="ml-2">
              {attachments.length + links.length || rlp.pdfs_downloaded}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact">
            Contact
          </TabsTrigger>
          <TabsTrigger value="candidates" data-testid="tab-candidates">
            Candidates
            <Badge tone="neutral" className="ml-2">
              {candidates.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="portfolio-matches" data-testid="tab-portfolio-matches">
            Portfolio matches
          </TabsTrigger>
          <TabsTrigger value="outreach" data-testid="tab-outreach">
            Outreach
            <Badge
              tone={
                drafts.some((d) => d.status === 'pending_approval') ? 'yellow' : 'neutral'
              }
              className="ml-2"
            >
              {drafts.filter((d) => d.status === 'pending_approval').length || drafts.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">
            Runs
            <Badge tone="neutral" className="ml-2">
              {runs.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notice">
          <NoticePanel
            classification={classification}
            description={description}
            initiative={noticeMeta.initiative}
          />
        </TabsContent>

        <TabsContent value="requirements">
          <RequirementsPanel
            rlpId={id}
            requirements={requirements}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['rlp', id] })}
          />
        </TabsContent>

        <TabsContent value="attachments">
          <AttachmentsAndLinksPanel
            attachments={attachments}
            links={links}
            samUrl={rlp.sam_url ?? null}
            solicitation={rlp.solicitation_number}
          />
        </TabsContent>

        <TabsContent value="contact">
          <ContactsPanel
            primary={primary}
            alternate={alternate}
            office={office}
            solicitation={rlp.solicitation_number}
          />
        </TabsContent>

        <TabsContent value="candidates">
          <CandidatesPanel
            rlpId={id}
            candidates={candidates}
            polygon={requirements?.delineated_area_geojson ?? null}
            selectedId={selectedCandidateId}
            onSelect={setSelectedCandidateId}
          />
        </TabsContent>

        <TabsContent value="portfolio-matches">
          <PortfolioMatchesPanel
            rlpId={id}
            extracted={rlp.extraction_status === 'extracted'}
          />
        </TabsContent>

        <TabsContent value="outreach">
          <OutreachTab
            drafts={drafts}
            selectedDraftId={selectedDraftId}
            onSelectDraft={setSelectedDraftId}
            onRefresh={() => queryClient.invalidateQueries({ queryKey: ['rlp', id] })}
          />
        </TabsContent>

        <TabsContent value="runs">
          {runs.length === 0 ? (
            <EmptyState title="No runs recorded" />
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="e-table w-full">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Status</th>
                      <th>Started</th>
                      <th className="text-right">Cost</th>
                      <th>Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.id}>
                        <td className="text-[0.8125rem] font-semibold text-ink">{r.agent_name}</td>
                        <td>
                          <Badge
                            tone={
                              r.status === 'success' ? 'green' : r.status === 'failed' ? 'red' : 'blue'
                            }
                            dot
                          >
                            {r.status}
                          </Badge>
                        </td>
                        <td className="whitespace-nowrap text-xs text-ink-muted">
                          {fmtRelative(r.started_at)}
                        </td>
                        <td className="text-right font-mono text-[0.8125rem] tabular-nums">
                          {fmtUsd(r.cost_usd)}
                        </td>
                        <td className="text-xs text-ink-muted">
                          {r.output_summary ?? r.error_message ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ========================================================================== */
/* HEADER SUBCOMPONENTS                                                        */
/* ========================================================================== */

function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      className={
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[0.6875rem] font-semibold ' +
        (active
          ? 'bg-success-soft text-success-bold ring-1 ring-inset ring-success-border'
          : 'bg-ink-muted/10 text-ink-muted ring-1 ring-inset ring-rule')
      }
    >
      <span
        className={
          'h-1.5 w-1.5 rounded-full ' + (active ? 'bg-success-bold animate-pulse' : 'bg-ink-dim')
        }
        aria-hidden
      />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function MetaItem({
  label,
  value,
  emphasis,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  emphasis?: boolean;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="kicker truncate">{label}</div>
      <div
        className={
          'mt-1 truncate ' +
          (emphasis
            ? 'text-[0.9375rem] font-bold text-ink'
            : 'text-[0.8125rem] font-semibold text-ink') +
          (mono ? ' font-mono' : '')
        }
        title={value ?? undefined}
      >
        {value && value.length > 0 ? value : '—'}
      </div>
    </div>
  );
}

function OrgStripItem({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="px-5 py-4">
      <div className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-brand-700">
        {label}
      </div>
      <div className="mt-1 text-[0.9375rem] font-semibold text-ink">{value ?? '—'}</div>
    </div>
  );
}

/* ========================================================================== */
/* NOTICE PANEL (new)                                                          */
/* ========================================================================== */

interface ClassificationData {
  set_aside: string | null;
  product_service_code: string | null;
  product_service_desc: string | null;
  naics_code: string | null;
  naics_desc: string | null;
  place_of_performance: string | null;
}

function NoticePanel({
  classification,
  description,
  initiative,
}: {
  classification: ClassificationData;
  description: string | null;
  initiative: string | null;
}) {
  return (
    <div className="flex flex-col gap-6">
      {/* Classification */}
      <section className="card flex flex-col gap-5 p-6">
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-brand-50 text-brand-700">
            <Tag className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-[1.0625rem] font-bold tracking-head text-ink">Classification</h3>
            <p className="text-xs text-ink-muted">Set-aside, codes, and place of performance.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          <ClassField label="Original Set Aside" value={classification.set_aside} />
          <ClassField
            label="Product Service Code"
            value={
              classification.product_service_code
                ? `${classification.product_service_code}${
                    classification.product_service_desc
                      ? ` · ${classification.product_service_desc}`
                      : ''
                  }`
                : null
            }
          />
          <ClassField
            label="NAICS Code"
            value={
              classification.naics_code
                ? `${classification.naics_code}${
                    classification.naics_desc ? ` · ${classification.naics_desc}` : ''
                  }`
                : null
            }
          />
          <ClassField
            label="Place of Performance"
            value={classification.place_of_performance}
            full
            icon={MapPin}
          />
          {initiative && <ClassField label="Presidential Initiative" value={initiative} />}
        </div>
      </section>

      {/* Description */}
      <section className="card flex flex-col gap-4 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-brand-50 text-brand-700">
              <FileText className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-[1.0625rem] font-bold tracking-head text-ink">Description</h3>
              <p className="text-xs text-ink-muted">Full notice text as published on SAM.gov.</p>
            </div>
          </div>
        </div>
        {description ? (
          <div className="whitespace-pre-wrap text-[0.9375rem] leading-relaxed text-ink">
            {description}
          </div>
        ) : (
          <EmptyState
            title="Description pending"
            description="We'll surface the full SAM.gov description here once the extractor completes."
          />
        )}
      </section>
    </div>
  );
}

function ClassField({
  label,
  value,
  full,
  icon: Icon,
}: {
  label: string;
  value: string | null;
  full?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className={full ? 'sm:col-span-2 lg:col-span-3' : ''}>
      <div className="kicker mb-1.5">{label}</div>
      <div className="flex items-start gap-2 rounded-lg border border-rule bg-surface-sunk px-3 py-2.5 text-[0.875rem] font-semibold text-ink">
        {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />}
        <span className="min-w-0 flex-1 break-words">{value ?? '—'}</span>
      </div>
    </div>
  );
}

/* ========================================================================== */
/* REQUIREMENTS PANEL (unchanged behavior)                                     */
/* ========================================================================== */

function RequirementsPanel({
  rlpId,
  requirements,
  onSaved,
}: {
  rlpId: string;
  requirements: RLPRequirements | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<RLPRequirements>>({});

  const mutation = useMutation({
    mutationFn: (patch: Partial<RLPRequirements>) => api.patchRequirements(rlpId, patch),
    onSuccess: () => {
      setEditing(false);
      setDraft({});
      onSaved();
    },
  });

  if (!requirements) {
    return (
      <EmptyState
        title="Not yet extracted"
        description="Requirements will appear here once the extractor runs."
      />
    );
  }

  const v = { ...requirements, ...draft };
  const field = <K extends keyof RLPRequirements>(key: K, next: RLPRequirements[K]) =>
    setDraft((prev) => ({ ...prev, [key]: next }));

  return (
    <div className="card flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[0.9375rem] font-semibold text-ink">Extracted requirements</h3>
          <p className="text-xs text-ink-muted">
            Structured fields the extractor pulled from the RLP PDF.
          </p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditing(false);
                  setDraft({});
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => mutation.mutate(draft)}
                disabled={mutation.isPending || Object.keys(draft).length === 0}
                data-testid="save-requirements"
              >
                <Save className="h-3.5 w-3.5" />
                {mutation.isPending ? 'Saving…' : 'Save & re-run scout'}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="edit-requirements"
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
        <NumField
          label="ABOA min (sf)"
          value={v.aboa_sf_min}
          editing={editing}
          onChange={(n) => field('aboa_sf_min', n)}
        />
        <NumField
          label="ABOA max (sf)"
          value={v.aboa_sf_max}
          editing={editing}
          onChange={(n) => field('aboa_sf_max', n)}
        />
        <NumField
          label="Security level"
          value={v.security_level}
          editing={editing}
          onChange={(n) => field('security_level', n)}
        />
        <NumField
          label="Setback required (ft)"
          value={v.setback_required_ft ?? null}
          editing={editing}
          onChange={(n) => field('setback_required_ft', n)}
        />
        <NumField
          label="Parking — reserved"
          value={v.parking_reserved ?? 0}
          editing={editing}
          onChange={(n) => field('parking_reserved', n)}
        />
        <NumField
          label="Parking — surface"
          value={v.parking_surface ?? 0}
          editing={editing}
          onChange={(n) => field('parking_surface', n)}
        />
        <NumField
          label="Lease total (yrs)"
          value={v.lease_term_total_years ?? 0}
          editing={editing}
          onChange={(n) => field('lease_term_total_years', n)}
        />
        <NumField
          label="Lease firm (yrs)"
          value={v.lease_term_firm_years ?? 0}
          editing={editing}
          onChange={(n) => field('lease_term_firm_years', n)}
        />
      </div>

      <div className="border-t border-rule pt-5">
        <div className="kicker mb-2">Delineated area</div>
        {editing ? (
          <Textarea
            value={v.delineated_area_description ?? ''}
            onChange={(e) => field('delineated_area_description', e.target.value)}
          />
        ) : (
          <p className="text-[0.9375rem] leading-relaxed text-ink">
            {v.delineated_area_description ?? '—'}
          </p>
        )}
      </div>

      <div className="border-t border-rule pt-5">
        <div className="kicker mb-2">Special requirements</div>
        <div className="flex flex-wrap gap-2">
          {(v.special_requirements ?? []).length === 0 ? (
            <span className="text-sm text-ink-dim">—</span>
          ) : (
            (v.special_requirements ?? []).map((s, i) => (
              <Badge key={i} tone="neutral">
                {s}
              </Badge>
            ))
          )}
        </div>
      </div>

      {mutation.isError && (
        <p className="text-xs text-danger-bold">
          Save failed: {mutation.error instanceof Error ? mutation.error.message : 'unknown'}
        </p>
      )}
    </div>
  );
}

function NumField({
  label,
  value,
  editing,
  onChange,
}: {
  label: string;
  value: number | null;
  editing: boolean;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <div className="kicker mb-1.5">{label}</div>
      {editing ? (
        <Input
          type="number"
          className="font-mono tabular-nums"
          value={value ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      ) : (
        <div className="text-[1.5rem] font-bold tracking-head text-ink tabular-nums">
          {value == null ? '—' : fmtInt(value)}
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */
/* CANDIDATES PANEL                                                            */
/* ========================================================================== */

function CandidatesPanel({
  rlpId,
  candidates,
  polygon,
  selectedId,
  onSelect,
}: {
  rlpId: string;
  candidates: CandidateProperty[];
  polygon: RLPRequirements['delineated_area_geojson'];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const profiles = useOfferorProfiles(rlpId);
  const profile = profiles.data?.profiles?.[0];
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  if (candidates.length === 0) {
    return (
      <EmptyState
        title="No candidates yet"
        description="The property scout will populate this once it runs."
      />
    );
  }
  return (
    <div className="flex flex-col gap-6">
      <PolygonMap
        polygon={polygon}
        candidates={candidates}
        selectedId={selectedId}
        onSelect={onSelect}
      />
      {!profile && !profiles.isLoading && (
        <div className="flex flex-col gap-3 rounded-xl border border-info-border bg-info-soft px-4 py-3 text-[0.8125rem] text-info-bold sm:flex-row sm:items-center sm:justify-between">
          <span>
            An offeror profile (legal entity + point of contact) is required before any form
            fill can run for this RLP.
          </span>
          <button
            type="button"
            onClick={() => setProfileDialogOpen(true)}
            className="inline-flex items-center justify-center gap-1 self-start rounded-md border border-info-border bg-info-soft px-3 py-1.5 text-[0.75rem] font-semibold text-info-bold shadow-sm hover:bg-info-border/40"
            data-testid="rlp-create-profile"
          >
            Create profile
          </button>
        </div>
      )}
      <OfferorProfileDialog
        rlpId={rlpId}
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
      />
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="e-table w-full">
            <thead>
              <tr>
                <th className="w-20">Score</th>
                <th>Property</th>
                <th>Class</th>
                <th className="text-right">RSF</th>
                <th>Source</th>
                <th>Flags</th>
                <th>Submission</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr
                  key={c.id}
                  data-selected={selectedId === c.id}
                  onClick={() => onSelect(c.id)}
                  className="cursor-pointer"
                  data-testid={`candidate-row-${c.id}`}
                >
                  <td>
                    <ScoreDot score={c.match_score} />
                  </td>
                  <td>
                    <div className="text-[0.8125rem] font-semibold text-ink">{c.address}</div>
                    <div className="mt-0.5 text-xs text-ink-muted">
                      {c.city}, {c.state} {c.zip}
                    </div>
                  </td>
                  <td className="text-[0.8125rem] text-ink-muted">{c.class ?? '—'}</td>
                  <td className="text-right font-mono text-[0.8125rem] tabular-nums">
                    {fmtInt(c.rentable_sf)}
                  </td>
                  <td>
                    <Badge tone="neutral">{c.source}</Badge>
                  </td>
                  <td className="space-x-1.5">
                    {c.setback_risk ? <Badge tone="red">setback</Badge> : null}
                    {!c.in_polygon ? <Badge tone="yellow">out of area</Badge> : null}
                    {!c.size_match ? <Badge tone="yellow">size</Badge> : null}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <FormFillLauncher
                      rlpId={rlpId}
                      candidateId={c.id}
                      offerorProfileId={profile?.id ?? null}
                    />
                  </td>
                  <td>
                    {c.source_url && (
                      <a
                        href={c.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[0.75rem] font-semibold text-brand-700 hover:text-brand-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3 w-3" /> source
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FormFillLauncher({
  rlpId,
  candidateId,
  offerorProfileId,
}: {
  rlpId: string;
  candidateId: string;
  offerorProfileId: string | null;
}) {
  const start = useStartFormFill();
  const disabled = !offerorProfileId || start.isPending;

  const launch = () => {
    if (!offerorProfileId) return;
    start.mutate(
      {
        rlp_opportunity_id: rlpId,
        candidate_property_id: candidateId,
        offeror_profile_id: offerorProfileId,
      },
      {
        onSuccess: (res) => {
          // Use a simple navigation — no router dependency needed inside the
          // mutation callback, and the new page is on the same origin.
          window.location.assign(`/forms/${res.package_id}`);
        },
      }
    );
  };

  return (
    <button
      type="button"
      onClick={launch}
      disabled={disabled}
      title={
        offerorProfileId
          ? 'Start filling the GSA 527 package for this property'
          : 'Add an offeror profile for this RLP first'
      }
      className="inline-flex items-center gap-1 rounded-md border border-brand-300 bg-brand-50 px-2.5 py-1 text-[0.75rem] font-semibold text-brand-800 transition-colors hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-50"
      data-testid={`start-form-fill-${candidateId}`}
    >
      {start.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <FileCheck2 className="h-3 w-3" />
      )}
      {start.isPending ? 'Queuing…' : 'Start form fill'}
    </button>
  );
}

function ScoreDot({ score }: { score: number | null }) {
  if (score == null) {
    return <div className="text-sm text-ink-dim">—</div>;
  }
  const color =
    score >= 80
      ? 'text-success-bold ring-success-border bg-success-soft'
      : score >= 60
        ? 'text-warn-bold ring-warn-border bg-warn-soft'
        : 'text-danger-bold ring-danger-border bg-danger-soft';
  return (
    <div
      className={
        'grid h-11 w-11 place-items-center rounded-full ring-2 ring-inset text-sm font-bold tabular-nums ' +
        color
      }
    >
      {score}
    </div>
  );
}

/* ========================================================================== */
/* RESOLVERS / STUBS                                                           */
/* ========================================================================== */

function resolveAttachments(rlp: RLPOpportunity): RLPAttachment[] {
  if (rlp.attachments && rlp.attachments.length > 0) return rlp.attachments;
  if (rlp.pdfs_downloaded > 0) {
    return Array.from({ length: rlp.pdfs_downloaded }).map((_, i) => ({
      id: `${rlp.id}-pdf-${i}`,
      name:
        i === 0
          ? `RLP ${rlp.solicitation_number}.pdf`
          : `${rlp.solicitation_number}-exhibit-${i}.pdf`,
      url: rlp.sam_url ?? '#',
      kind: i === 0 ? 'rlp' : 'exhibit',
      bytes: null,
      access: 'Public',
      updated_at: rlp.updated_at,
    }));
  }
  return [];
}

function resolveLinks(rlp: RLPOpportunity): RLPLink[] {
  if (rlp.related_links && rlp.related_links.length > 0) return rlp.related_links;
  if (rlp.sam_url) {
    return [
      {
        id: `${rlp.id}-sam`,
        name: 'SAM.gov Notice Listing',
        url: rlp.sam_url,
        updated_at: rlp.updated_at,
      },
    ];
  }
  return [];
}

function resolveContact(rlp: RLPOpportunity): RLPContact | null {
  if (rlp.primary_contact) return rlp.primary_contact;
  return null;
}

function resolveAlternate(rlp: RLPOpportunity): RLPContact | null {
  return rlp.alternative_contact ?? null;
}

function resolveOfficeAddress(rlp: RLPOpportunity): RLPOfficeAddress | null {
  if (rlp.contracting_office_address) return rlp.contracting_office_address;
  if (rlp.sub_agency || rlp.office) {
    return {
      office_name: rlp.office ?? `${rlp.sub_agency ?? 'Contracting'} Office`,
      street: null,
      city: rlp.city ?? null,
      state: rlp.state ?? null,
      zip: null,
      country: 'USA',
    };
  }
  return null;
}

function resolveClassification(rlp: RLPOpportunity): ClassificationData {
  return {
    set_aside: rlp.set_aside ?? null,
    product_service_code: rlp.product_service_code ?? null,
    product_service_desc: rlp.product_service_desc ?? null,
    naics_code: rlp.naics_code ?? null,
    naics_desc: rlp.naics_desc ?? null,
    place_of_performance:
      rlp.place_of_performance ??
      (rlp.city && rlp.state ? `${rlp.city}, ${rlp.state}` : (rlp.state ?? null)),
  };
}

interface NoticeMeta {
  contract_type: string | null;
  set_aside: string | null;
  inactive_policy: string | null;
  related_notice: string | null;
  contract_line_item_number: string | null;
  initiative: string | null;
}

function resolveNoticeMeta(rlp: RLPOpportunity): NoticeMeta {
  return {
    contract_type: rlp.contract_type ?? deriveContractType(rlp),
    set_aside: rlp.set_aside ?? null,
    inactive_policy: rlp.inactive_policy ?? null,
    related_notice: rlp.related_notice ?? null,
    contract_line_item_number: rlp.contract_line_item_number ?? null,
    initiative: rlp.initiative ?? null,
  };
}

function deriveContractType(rlp: RLPOpportunity): string {
  if (rlp.mod_number > 0) return 'Solicitation (Updated)';
  if (rlp.extraction_status === 'extracted') return 'Combined Synopsis/Solicitation';
  return 'Solicitation';
}

function resolveDescription(rlp: RLPOpportunity): string | null {
  return rlp.description ?? null;
}

/* ========================================================================== */
/* CONTACTS PANEL                                                              */
/* ========================================================================== */

function ContactsPanel({
  primary,
  alternate,
  office,
  solicitation,
}: {
  primary: RLPContact | null;
  alternate: RLPContact | null;
  office: RLPOfficeAddress | null;
  solicitation: string;
}) {
  const hasPrimary = primary && (primary.name || primary.email || primary.phone);
  const hasAlt = alternate && (alternate.name || alternate.email || alternate.phone);

  if (!hasPrimary && !hasAlt && !office) {
    return (
      <EmptyState
        title="No contact information on file"
        description="The extractor will surface POCs and the contracting office address once parsed from the RLP."
        icon={<Landmark className="h-5 w-5" />}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <ContactCard
        title="Primary Point of Contact"
        accent="primary"
        contact={primary}
        solicitation={solicitation}
      />
      <ContactCard
        title="Alternative Point of Contact"
        accent="alt"
        contact={alternate}
        solicitation={solicitation}
      />
      <div className="lg:col-span-2">
        <OfficeAddressCard office={office} />
      </div>
    </div>
  );
}

function ContactCard({
  title,
  accent,
  contact,
  solicitation,
}: {
  title: string;
  accent: 'primary' | 'alt';
  contact: RLPContact | null;
  solicitation: string;
}) {
  const present = contact && (contact.name || contact.email || contact.phone);
  return (
    <section className="card flex flex-col gap-5 p-6">
      <div className="flex items-start gap-4">
        <div
          className={
            'grid h-12 w-12 shrink-0 place-items-center rounded-xl text-white shadow-card ' +
            (accent === 'primary'
              ? 'bg-gradient-to-br from-brand-500 to-brand-700'
              : 'bg-gradient-to-br from-slate-500 to-slate-700')
          }
        >
          <UserRound className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="kicker text-brand-700">{title}</div>
          <h3 className="mt-0.5 text-[1.0625rem] font-bold tracking-head text-ink">
            {contact?.name ?? (accent === 'primary' ? 'Contracting officer' : 'Alternate POC')}
          </h3>
          {contact?.role && (
            <div className="mt-0.5 text-[0.8125rem] text-ink-muted">{contact.role}</div>
          )}
          {contact?.office && <div className="text-xs text-ink-muted">{contact.office}</div>}
        </div>
      </div>

      {present ? (
        <>
          <div className="grid grid-cols-1 gap-3 border-t border-rule pt-5 sm:grid-cols-2">
            <ContactLine
              label="Email"
              icon={Mail}
              value={contact?.email ?? null}
              href={
                contact?.email
                  ? `mailto:${contact.email}?subject=${encodeURIComponent(
                      `Question regarding RLP ${solicitation}`
                    )}`
                  : null
              }
              copyable
            />
            <ContactLine
              label="Phone"
              icon={Phone}
              value={contact?.phone ?? null}
              href={contact?.phone ? `tel:${contact.phone}` : null}
              copyable
            />
          </div>

          {contact?.email && (
            <div className="flex flex-wrap gap-2 border-t border-rule pt-5">
              <Button asChild size="sm">
                <a
                  href={`mailto:${contact.email}?subject=${encodeURIComponent(
                    `Question regarding RLP ${solicitation}`
                  )}`}
                  data-testid={accent === 'primary' ? 'poc-email' : 'alt-poc-email'}
                >
                  <Mail className="h-3.5 w-3.5" /> Compose email
                </a>
              </Button>
              <Button asChild variant="secondary" size="sm">
                <a
                  href={`mailto:${contact.email}?subject=${encodeURIComponent(
                    `RLP ${solicitation} — site tour request`
                  )}&body=${encodeURIComponent(
                    `Hello${contact.name ? ` ${contact.name}` : ''},\n\nWe're reviewing RLP ${solicitation} and would like to coordinate a site tour. Please let us know your availability.\n\nThank you,\n`
                  )}`}
                >
                  Request site tour
                </a>
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-rule bg-surface-sunk px-4 py-6 text-center text-[0.8125rem] text-ink-muted">
          {accent === 'primary'
            ? 'Primary POC not yet extracted.'
            : 'No alternate POC listed for this solicitation.'}
        </div>
      )}
    </section>
  );
}

function ContactLine({
  label,
  icon: Icon,
  value,
  href,
  copyable,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  value: string | null;
  href: string | null;
  copyable?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-rule bg-surface-sunk p-3">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface text-ink-muted">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="kicker">{label}</div>
        {value ? (
          href ? (
            <a
              href={href}
              className="block truncate text-[0.875rem] font-semibold text-brand-700 hover:text-brand-800"
            >
              {value}
            </a>
          ) : (
            <div className="truncate text-[0.875rem] font-semibold text-ink">{value}</div>
          )
        ) : (
          <div className="text-[0.875rem] text-ink-dim">—</div>
        )}
      </div>
      {value && copyable && (
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(value);
          }}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-dim hover:bg-surface hover:text-brand-700"
          aria-label={`Copy ${label.toLowerCase()}`}
          title={`Copy ${label.toLowerCase()}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function OfficeAddressCard({ office }: { office: RLPOfficeAddress | null }) {
  if (!office) return null;
  const cityLine = [office.city, office.state].filter(Boolean).join(', ');
  const lines = [
    office.street,
    [cityLine, office.zip].filter(Boolean).join(' ').trim(),
    office.country,
  ].filter((line): line is string => !!line && line.length > 0);

  return (
    <section className="card flex flex-col gap-4 p-6 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-card">
          <Landmark className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="kicker text-brand-700">Contracting Office Address</div>
          <h3 className="mt-0.5 text-[1.0625rem] font-bold tracking-head text-ink">
            {office.office_name ?? 'Contracting Office'}
          </h3>
          {lines.length === 0 ? (
            <div className="mt-1 text-[0.875rem] text-ink-dim">Address not yet extracted.</div>
          ) : (
            <address className="mt-2 whitespace-pre-line text-[0.9375rem] not-italic leading-relaxed text-ink">
              {lines.join('\n')}
            </address>
          )}
        </div>
      </div>
      {lines.length > 0 && (
        <Button variant="secondary" size="sm" asChild>
          <a
            href={`https://www.google.com/maps/search/${encodeURIComponent(
              [office.office_name, ...lines].filter(Boolean).join(' ')
            )}`}
            target="_blank"
            rel="noreferrer"
          >
            <MapPin className="h-3.5 w-3.5" /> View on map
          </a>
        </Button>
      )}
    </section>
  );
}

/* ========================================================================== */
/* ATTACHMENTS / LINKS PANEL                                                   */
/* ========================================================================== */

function AttachmentsAndLinksPanel({
  attachments,
  links,
  samUrl,
  solicitation,
}: {
  attachments: RLPAttachment[];
  links: RLPLink[];
  samUrl: string | null;
  solicitation: string;
}) {
  const hasAny = attachments.length > 0 || links.length > 0;
  if (!hasAny) {
    return (
      <EmptyState
        title="No attachments or links on file"
        description="Attachments will appear here after the hunter downloads them from SAM.gov."
        icon={<FileText className="h-5 w-5" />}
        action={
          samUrl ? (
            <Button asChild variant="secondary" size="sm">
              <a href={samUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5" /> Open on SAM.gov
              </a>
            </Button>
          ) : null
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Links */}
      {links.length > 0 && (
        <section className="card overflow-hidden">
          <div className="flex items-center gap-2 border-b border-rule bg-surface-sunk px-5 py-3">
            <Link2 className="h-4 w-4 text-brand-700" />
            <h3 className="text-[0.9375rem] font-bold text-ink">Links</h3>
            <Badge tone="neutral" className="ml-1">
              {links.length}
            </Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="e-table w-full">
              <thead>
                <tr>
                  <th>Link Name</th>
                  <th className="whitespace-nowrap">Updated Date</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {links.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-[0.875rem] font-semibold text-brand-700 hover:text-brand-800"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {l.name}
                      </a>
                    </td>
                    <td className="whitespace-nowrap text-xs text-ink-muted">
                      {l.updated_at ? fmtDate(l.updated_at) : '—'}
                    </td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Attachments */}
      {attachments.length > 0 && (
        <section className="card overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-rule bg-surface-sunk px-5 py-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-brand-700" />
              <h3 className="text-[0.9375rem] font-bold text-ink">Attachments</h3>
              <Badge tone="neutral" className="ml-1">
                {attachments.length}
              </Badge>
            </div>
            <Button asChild size="sm" variant="secondary" data-testid="download-all-attachments">
              <a
                href={samUrl ?? '#'}
                target={samUrl ? '_blank' : undefined}
                rel={samUrl ? 'noreferrer' : undefined}
              >
                <Download className="h-3.5 w-3.5" /> Download All
              </a>
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="e-table w-full">
              <thead>
                <tr>
                  <th>Document</th>
                  <th className="whitespace-nowrap text-right">File Size</th>
                  <th>Access</th>
                  <th className="whitespace-nowrap">Updated Date</th>
                  <th className="w-12" />
                </tr>
              </thead>
              <tbody>
                {attachments.map((a) => (
                  <tr key={a.id} data-testid={`attachment-row-${a.id}`}>
                    <td>
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2.5"
                      >
                        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-danger-soft text-danger-bold ring-1 ring-inset ring-danger-border">
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-[0.875rem] font-semibold text-brand-700 hover:text-brand-800">
                              {a.name}
                            </span>
                            {a.kind && (
                              <Badge tone={a.kind === 'rlp' ? 'blue' : 'neutral'}>{a.kind}</Badge>
                            )}
                          </div>
                          <div className="text-[0.6875rem] text-ink-muted">RLP {solicitation}</div>
                        </div>
                      </a>
                    </td>
                    <td className="whitespace-nowrap text-right font-mono text-[0.8125rem] tabular-nums text-ink">
                      {typeof a.bytes === 'number' ? formatBytes(a.bytes) : '—'}
                    </td>
                    <td>
                      <AccessPill access={a.access ?? 'Public'} />
                    </td>
                    <td className="whitespace-nowrap text-xs text-ink-muted">
                      {a.updated_at ? fmtDate(a.updated_at) : '—'}
                    </td>
                    <td>
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        className="grid h-8 w-8 place-items-center rounded-md text-ink-muted hover:bg-surface-sunk hover:text-brand-700"
                        aria-label={`Download ${a.name}`}
                      >
                        <Download className="h-4 w-4" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function AccessPill({ access }: { access: 'Public' | 'Controlled' }) {
  const isPublic = access === 'Public';
  return (
    <span
      className={
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[0.6875rem] font-semibold ring-1 ring-inset ' +
        (isPublic
          ? 'bg-success-soft text-success-bold ring-success-border'
          : 'bg-warn-soft text-warn-bold ring-warn-border')
      }
    >
      <span
        className={'h-1.5 w-1.5 rounded-full ' + (isPublic ? 'bg-success-bold' : 'bg-warn-bold')}
        aria-hidden
      />
      {access}
    </span>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

/* ========================================================================== */
/* PORTFOLIO MATCHES PANEL — Sprint 10 Task 14                                 */
/* ========================================================================== */

function PortfolioMatchesPanel({
  rlpId,
  extracted,
}: {
  rlpId: string;
  extracted: boolean;
}) {
  const portfolio = useQuery({
    queryKey: ['portfolio'],
    queryFn: api.listPortfolio,
    enabled: extracted,
  });

  if (!extracted) {
    return (
      <EmptyState
        title="Extract this RLP first"
        description="Portfolio matches can only be scored once the extractor has pulled the RLP's requirements (polygon, ABOA band, parking, security tier, etc.)."
      />
    );
  }

  if (portfolio.isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="card flex flex-col gap-2 p-5">
            <SkeletonLine width="40%" />
            <SkeletonLine width="70%" />
          </div>
        ))}
      </div>
    );
  }

  if (!portfolio.data || portfolio.data.properties.length === 0) {
    return (
      <EmptyState
        title="No portfolio properties yet"
        description="Add a property in your Portfolio to see how it matches this RLP. Scoring uses the polygon, size band, parking, and security tier from the extracted requirements."
        action={
          <Button asChild size="sm">
            <Link to="/portfolio">Add a property</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="rlp-portfolio-matches">
      {portfolio.data.properties.map((p) => (
        <section key={p.id} className="flex flex-col gap-2">
          <header className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-[1rem] font-semibold text-ink">{p.name}</h3>
              <p className="truncate text-[0.75rem] text-ink-muted">
                {p.address} · {p.city}, {p.state} {p.zip}
              </p>
            </div>
            <Link
              to="/portfolio"
              className="text-[0.75rem] font-semibold text-brand-700 hover:text-brand-800"
            >
              Edit property
            </Link>
          </header>
          <AdjustToMatchPanel propertyId={p.id} rlpId={rlpId} />
        </section>
      ))}
    </div>
  );
}

/* ========================================================================== */
/* OUTREACH TAB — triage drafts scoped to this RLP                             */
/* ========================================================================== */

function OutreachTab({
  drafts,
  selectedDraftId,
  onSelectDraft,
  onRefresh,
}: {
  drafts: OutreachDraft[];
  selectedDraftId: string | null;
  onSelectDraft: (id: string) => void;
  onRefresh: () => void;
}) {
  const pending = drafts.filter((d) => d.status === 'pending_approval');
  const others = drafts.filter((d) => d.status !== 'pending_approval');
  const ordered = [...pending, ...others];
  const activeId = selectedDraftId ?? ordered[0]?.id ?? null;
  const selected = ordered.find((d) => d.id === activeId) ?? null;

  if (drafts.length === 0) {
    return (
      <EmptyState
        title="No outreach drafts yet"
        description="Once the writer drafts letters for candidate properties, they'll land here for your review. Nothing sends without your approval."
      />
    );
  }

  return (
    <div
      className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr]"
      data-testid="rlp-outreach-triage"
    >
      {/* Left: draft list */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-rule bg-surface-sunk px-4 py-3">
          <div className="text-[0.6875rem] font-semibold uppercase tracking-kicker text-ink-muted">
            {pending.length} pending
            {others.length > 0 ? ` · ${others.length} actioned` : ''}
          </div>
          {pending.length > 0 && (
            <Badge tone="yellow" dot>
              Review
            </Badge>
          )}
        </div>
        <ul className="max-h-[70vh] overflow-y-auto scroll-elegant">
          {ordered.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => onSelectDraft(d.id)}
                className={cn(
                  'flex w-full flex-col gap-1.5 border-b border-rule px-4 py-3.5 text-left transition-colors hover:bg-surface-sunk',
                  d.id === activeId &&
                    'bg-brand-50 hover:bg-brand-50 dark:bg-brand-900/40 dark:hover:bg-brand-900/40'
                )}
              >
                <div className="flex items-center gap-2">
                  <div className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[0.625rem] font-bold text-white">
                    {(d.recipient_name ?? '?').slice(0, 2).toUpperCase()}
                  </div>
                  <span className="flex-1 truncate text-[0.8125rem] font-semibold text-ink">
                    {d.recipient_name ?? 'Unknown'}
                  </span>
                  <DraftStatusPill status={d.status} />
                </div>
                <div className="truncate text-[0.8125rem] text-ink">{d.subject}</div>
                <div className="flex items-center justify-between gap-2 text-xs text-ink-muted">
                  <span className="truncate">{d.candidate_address ?? 'Unknown address'}</span>
                  <span className="shrink-0 text-[0.6875rem] text-ink-dim">
                    {fmtRelative(d.created_at)}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Right: reader */}
      <div className="card overflow-hidden">
        {selected ? (
          selected.status === 'pending_approval' ? (
            <DraftReader
              key={selected.id}
              draft={selected}
              onDone={onRefresh}
              showSolicitationLink={false}
            />
          ) : (
            <ReadOnlyDraft draft={selected} />
          )
        ) : (
          <div className="grid h-full place-items-center p-10 text-sm text-ink-muted">
            Select a draft to review.
          </div>
        )}
      </div>
    </div>
  );
}

function DraftStatusPill({ status }: { status: OutreachDraft['status'] }) {
  const tone: 'yellow' | 'green' | 'red' | 'blue' | 'neutral' =
    status === 'pending_approval'
      ? 'yellow'
      : status === 'sent' || status === 'replied'
        ? 'green'
        : status === 'rejected' || status === 'bounced'
          ? 'red'
          : 'blue';
  return (
    <Badge tone={tone} dot>
      {status.replace('_', ' ')}
    </Badge>
  );
}

function ReadOnlyDraft({ draft }: { draft: OutreachDraft }) {
  return (
    <article className="flex h-full flex-col">
      <header className="border-b border-rule bg-surface-sunk px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[0.75rem] font-bold text-white">
              {(draft.recipient_name ?? '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[0.8125rem]">
                <span className="font-semibold text-ink">{draft.recipient_name ?? '—'}</span>
                <DraftStatusPill status={draft.status} />
              </div>
              <div className="truncate text-xs text-ink-muted">
                {draft.recipient_email ?? 'no email'}
              </div>
            </div>
          </div>
          <div className="text-right text-[0.6875rem] text-ink-muted">
            {draft.sent_at ? `Sent ${fmtRelative(draft.sent_at)}` : null}
            {draft.approved_at && !draft.sent_at
              ? `Approved ${fmtRelative(draft.approved_at)}`
              : null}
          </div>
        </div>
      </header>
      <div className="flex flex-1 flex-col gap-4 p-5">
        <div>
          <div className="kicker mb-1.5">Subject</div>
          <div className="text-[0.9375rem] font-semibold text-ink">{draft.subject}</div>
        </div>
        <div className="flex flex-1 flex-col">
          <div className="kicker mb-1.5">Body</div>
          <div className="whitespace-pre-wrap rounded-lg border border-rule bg-surface-sunk p-4 text-[0.9375rem] leading-relaxed text-ink">
            {draft.body}
          </div>
        </div>
        {draft.rejection_reason && (
          <div className="rounded-lg border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger-bold">
            Rejected: {draft.rejection_reason}
          </div>
        )}
      </div>
    </article>
  );
}
